/**
 * PlayerSummaryCardView - View for AI-generated player summary card
 *
 * Displays player summary with Generate button, language dropdown,
 * timestamp, truncation with "Read more", and full summary modal.
 * SE-5570: the language selector renders through the shared
 * `LanguageGenerateDropdown` molecule (on the SelectDropdown /
 * FixedPositionDropdownShell) instead of the retired gluestack Menu atom. The
 * shell owns native Modal hosting (web portals to the document body), so the
 * old `useRNModal={Platform.OS!=="web"}` fork — which existed to keep the
 * RN-Web Modal backdrop from swallowing the Delete-player overlay's pointer
 * events — is no longer needed.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { TEST_ID_AI_PLAYER_SUMMARY } from "e2e/testId";
import { Copy, Sparkles } from "lucide-react-native";
import { memo } from "react";

import { Pressable } from "@/components/atoms/Pressable";

import ScopedModal from "@/components/organisms/shared/ScopedModal";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";
import type { LanguageSelectorSaveDefault } from "@/components/molecules/LanguageSelector";
import LanguageGenerateDropdown from "@/features/player-detail/components/molecules/LanguageGenerateDropdown";

import {
  getContentArea,
  COPY_ICON_CLASS,
  getContentTextClasses,
  getIconClasses,
  getTimestampText,
  getTitleTextClasses,
  SUBTITLE_TEXT_CLASS,
} from "@/features/player-detail/utils/summaryCardStyles";

/**
 * Props for PlayerSummaryCardView component
 */
interface PlayerSummaryCardViewProps {
  /** Whether dark mode is active */
  isDark: boolean;
  /** Truncated summary content to display */
  summaryContent: string;
  /** Full summary content for modal */
  fullSummaryContent: string;
  /** Formatted last updated timestamp */
  lastUpdated: string | null;
  /** Whether summary is currently generating */
  isGenerating: boolean;
  /** Whether there was an error */
  hasError: boolean;
  /** Error message to display */
  errorMessage: string | null;
  /** Whether to show "Read more" link */
  showReadMore: boolean;
  /** Whether full summary modal is open */
  isModalOpen: boolean;
  /** Display label for selected language */
  selectedLanguageLabel: string;
  /** List of supported languages */
  supportedLanguages: { value: string; label: string }[];
  /** Currently selected language code */
  /**
   * The locale the dropdown marks, or `undefined` while nothing has resolved
   * (SE-6511). Not the generation locale — see `selectedLanguageLabel`, which
   * always names the language that will actually be produced.
   */
  selectedLanguage?: string;
  /** The save-as-default row, or undefined when nothing is overridden. */
  saveAsDefault?: LanguageSelectorSaveDefault;
  /** Callback when "Read more" is pressed */
  onReadMore: () => void;
  /** Callback when full summary modal is closed */
  onModalClose: () => void;
  /** Generates the summary in the chosen language */
  onSelectLanguage: (language: string) => void;
  /** Whether the generate trigger is disabled (generating) */
  isButtonDisabled: boolean;
  /** Callback when copy icon is pressed */
  onCopySummary: () => void;
}

/**
 * Get container background classes for player summary card
 * @param isDark - Dark theme flag
 * @returns Tailwind class string for container styling
 */
const getContainerClasses = (): string =>
  "flex-1 rounded-lg border border-outline-card bg-surface-card p-4";

/**
 * PlayerSummaryCardView renders the player summary card
 * @param props - View props
 * @param props.isDark - Whether dark mode is active
 * @param props.summaryContent - Truncated summary content
 * @param props.fullSummaryContent - Full summary for modal
 * @param props.lastUpdated - Formatted timestamp
 * @param props.isGenerating - Whether generating
 * @param props.hasError - Whether error occurred
 * @param props.errorMessage - Error message
 * @param props.showReadMore - Whether to show read more
 * @param props.isModalOpen - Whether modal is open
 * @param props.selectedLanguageLabel - Selected language display label
 * @param props.supportedLanguages - Available languages
 * @param props.selectedLanguage - The locale the dropdown marks, or undefined
 * @param props.saveAsDefault - The save-as-default row, or undefined
 * @param props.onReadMore - Read more callback
 * @param props.onModalClose - Modal close callback
 * @param props.onSelectLanguage - Generates the summary in the chosen language
 * @param props.isButtonDisabled - Whether the generate trigger is disabled
 * @param props.onCopySummary - Copy summary callback
 * @returns Box containing the player summary card
 */
const PlayerSummaryCardView = ({
  isDark,
  summaryContent,
  fullSummaryContent,
  lastUpdated,
  isGenerating,
  hasError,
  errorMessage,
  showReadMore,
  isModalOpen,
  supportedLanguages,
  selectedLanguage,
  saveAsDefault,
  selectedLanguageLabel,
  onReadMore,
  onModalClose,
  onSelectLanguage,
  isButtonDisabled,
  onCopySummary,
}: PlayerSummaryCardViewProps) => (
  <>
    <Box
      data-testid={TEST_ID_AI_PLAYER_SUMMARY.CONTAINER}
      className={getContainerClasses()}
    >
      <Stack className="flex-1 justify-between gap-3">
        {/* Top section: Header, timestamp, content */}
        <Stack className="gap-3">
          {/* Header row with title and Read more */}
          <Stack
            direction="horizontal"
            className="items-center justify-between"
          >
            <Stack direction="horizontal" className="items-center gap-2">
              <Icon
                as={Sparkles}
                size="sm"
                className={getIconClasses(isDark)}
              />
              <Text variant="title" className={getTitleTextClasses(isDark)}>
                Player Summary
              </Text>
            </Stack>
            {showReadMore && !isGenerating ? (
              <Pressable onPress={onReadMore}>
                <Text
                  variant="body-strong"
                  className="text-needs-semantic-primary-500"
                >
                  Read more
                </Text>
              </Pressable>
            ) : null}
          </Stack>

          {/* Timestamp with copy icon — hidden in empty state to avoid duplicate "no summary" messages */}
          {isGenerating || lastUpdated ? (
            <Stack direction="horizontal" className="items-center gap-2">
              <Text variant="unstyled" className={SUBTITLE_TEXT_CLASS}>
                {getTimestampText(isGenerating, lastUpdated)}
              </Text>
              {summaryContent && !isGenerating ? (
                <Pressable onPress={onCopySummary}>
                  <Icon as={Copy} size="xs" className={COPY_ICON_CLASS} />
                </Pressable>
              ) : null}
            </Stack>
          ) : null}

          {/* Content area */}
          {getContentArea(
            isDark,
            isGenerating,
            hasError,
            errorMessage,
            summaryContent
          )}
        </Stack>

        {/* Generate button with language dropdown */}
        <Stack direction="horizontal" className="items-center pt-2">
          <LanguageGenerateDropdown
            isDark={isDark}
            supportedLanguages={supportedLanguages}
            selectedLanguage={selectedLanguage}
            selectedLanguageLabel={selectedLanguageLabel}
            saveAsDefault={saveAsDefault}
            disabled={isButtonDisabled}
            onSelectLanguage={onSelectLanguage}
          />
        </Stack>
      </Stack>
    </Box>

    {/* Full Summary Modal - web: portal card, native: full-screen RN Modal */}
    <ScopedModal
      isOpen={isModalOpen}
      onClose={onModalClose}
      title="Player Summary"
      isDark={isDark}
      maxWidth={500}
    >
      <Text variant="unstyled" className={getContentTextClasses(isDark)}>
        {fullSummaryContent}
      </Text>
    </ScopedModal>
  </>
);

PlayerSummaryCardView.displayName = "PlayerSummaryCardView";

export default memo(PlayerSummaryCardView);
